import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Loader2, Save, Globe, Mail, CheckCircle, AlertCircle, Edit, Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const WebsiteSettingsSection = ({ api }) => {
  const [websiteTab, setWebsiteTab] = useState('config');
  const [smtp, setSmtp] = useState({ host: '', port: 587, username: '', password: '', from_email: '', from_name: 'iHostPMS', use_tls: true, start_tls: false });
  const [website, setWebsite] = useState({});
  const [firebase, setFirebase] = useState({ api_key: '', auth_domain: '', project_id: '', storage_bucket: '', messaging_sender_id: '', app_id: '', enabled: false });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpResult, setSmtpResult] = useState(null);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [webSaving, setWebSaving] = useState(false);
  const [firebaseSaving, setFirebaseSaving] = useState(false);
  const [legalTab, setLegalTab] = useState('privacy');

  useEffect(() => {
    const load = async () => {
      try {
        setSmtp(await api('/smtp'));
        setWebsite(await api('/website-config'));
        try { setFirebase(await api('/firebase-config')); } catch {}
      } catch (err) { console.error(err); }
    };
    load();
  }, []);

  const saveSmtp = async () => {
    setSmtpSaving(true);
    try { await api('/smtp', { method: 'PUT', body: JSON.stringify(smtp) }); }
    catch (err) { alert(err.message); }
    finally { setSmtpSaving(false); }
  };

  const testSmtp = async () => {
    if (!testEmailRecipient) return;
    setSmtpTesting(true); setSmtpResult(null);
    try {
      const result = await api('/smtp/test', { method: 'POST', body: JSON.stringify({ recipient: testEmailRecipient }) });
      setSmtpResult(result);
    } catch (err) { setSmtpResult({ success: false, error: err.message }); }
    finally { setSmtpTesting(false); }
  };

  const saveWebsite = async () => {
    setWebSaving(true);
    try { await api('/website-config', { method: 'PUT', body: JSON.stringify(website) }); }
    catch (err) { alert(err.message); }
    finally { setWebSaving(false); }
  };

  const saveFirebase = async () => {
    setFirebaseSaving(true);
    try { await api('/firebase-config', { method: 'PUT', body: JSON.stringify(firebase) }); alert('Firebase config saved!'); }
    catch (err) { alert(err.message); }
    finally { setFirebaseSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="platform-website-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Website Settings</h2>
          <p className="text-sm text-slate-500 mt-1">Configure website display and email settings</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={websiteTab === 'config' ? 'default' : 'outline'} size="sm"
            className={`rounded-xl ${websiteTab === 'config' ? 'bg-slate-800 text-white' : ''}`}
            onClick={() => setWebsiteTab('config')}><Globe className="w-4 h-4 mr-2" /> Website Config</Button>
          <Button variant={websiteTab === 'smtp' ? 'default' : 'outline'} size="sm"
            className={`rounded-xl ${websiteTab === 'smtp' ? 'bg-slate-800 text-white' : ''}`}
            onClick={() => setWebsiteTab('smtp')}><Mail className="w-4 h-4 mr-2" /> Email (SMTP)</Button>
          <Button variant={websiteTab === 'auth' ? 'default' : 'outline'} size="sm"
            className={`rounded-xl ${websiteTab === 'auth' ? 'bg-slate-800 text-white' : ''}`}
            onClick={() => setWebsiteTab('auth')}><Shield className="w-4 h-4 mr-2" /> Auth</Button>
          <Button variant={websiteTab === 'legal' ? 'default' : 'outline'} size="sm"
            className={`rounded-xl ${websiteTab === 'legal' ? 'bg-slate-800 text-white' : ''}`}
            onClick={() => setWebsiteTab('legal')}><Edit className="w-4 h-4 mr-2" /> Legal Pages</Button>
        </div>
      </div>

      {/* Website Config Tab */}
      {websiteTab === 'config' && (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 space-y-4">
          <h3 className="font-semibold text-slate-800">Landing Page Configuration</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-sm">Site Title</Label><Input value={website.site_title || ''} onChange={e => setWebsite(w => ({ ...w, site_title: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-sm">Tagline</Label><Input value={website.tagline || ''} onChange={e => setWebsite(w => ({ ...w, tagline: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-sm">Contact Email</Label><Input value={website.contact_email || ''} onChange={e => setWebsite(w => ({ ...w, contact_email: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-sm">Support Phone</Label><Input value={website.support_phone || ''} onChange={e => setWebsite(w => ({ ...w, support_phone: e.target.value }))} className="mt-1" /></div>
          </div>
          <h4 className="font-medium text-slate-700 mt-4">Section Visibility</h4>
          <div className="grid grid-cols-2 gap-3">
            {['show_features', 'show_pricing', 'show_testimonials', 'show_faq', 'show_stats', 'show_ai_features'].map(key => (
              <div key={key} className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-600 capitalize">{key.replace(/_/g, ' ').replace('show ', '')}</span>
                <Switch checked={website[key] !== false} onCheckedChange={checked => setWebsite(w => ({ ...w, [key]: checked }))} />
              </div>
            ))}
          </div>
          <Button onClick={saveWebsite} disabled={webSaving} className="bg-slate-800 hover:bg-slate-900 text-white mt-4">
            {webSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Website Config
          </Button>
        </div>
      )}

      {/* SMTP Tab */}
      {websiteTab === 'smtp' && (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 space-y-4 max-w-2xl">
          <h3 className="font-semibold text-slate-800">SMTP Email Configuration</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-sm">SMTP Host</Label><Input value={smtp.host || ''} onChange={e => setSmtp(s => ({ ...s, host: e.target.value }))} placeholder="smtp.gmail.com" className="mt-1" /></div>
            <div><Label className="text-sm">Port</Label><Input type="number" value={smtp.port || 587} onChange={e => setSmtp(s => ({ ...s, port: parseInt(e.target.value) }))} className="mt-1" /></div>
            <div><Label className="text-sm">Username</Label><Input value={smtp.username || ''} onChange={e => setSmtp(s => ({ ...s, username: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-sm">Password</Label><Input type="password" value={smtp.password || ''} onChange={e => setSmtp(s => ({ ...s, password: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-sm">From Email</Label><Input value={smtp.from_email || ''} onChange={e => setSmtp(s => ({ ...s, from_email: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-sm">From Name</Label><Input value={smtp.from_name || ''} onChange={e => setSmtp(s => ({ ...s, from_name: e.target.value }))} className="mt-1" /></div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2"><Switch checked={smtp.use_tls} onCheckedChange={c => setSmtp(s => ({ ...s, use_tls: c }))} /><Label className="text-sm">Use TLS</Label></div>
            <div className="flex items-center gap-2"><Switch checked={smtp.start_tls} onCheckedChange={c => setSmtp(s => ({ ...s, start_tls: c }))} /><Label className="text-sm">STARTTLS</Label></div>
          </div>
          <div className="flex gap-3">
            <Button onClick={saveSmtp} disabled={smtpSaving} className="bg-slate-800 hover:bg-slate-900 text-white">
              {smtpSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save SMTP
            </Button>
          </div>
          <div className="pt-4 border-t">
            <h4 className="font-medium text-slate-700 mb-2">Test Email</h4>
            <div className="flex gap-2">
              <Input value={testEmailRecipient} onChange={e => setTestEmailRecipient(e.target.value)} placeholder="test@example.com" className="flex-1" />
              <Button onClick={testSmtp} disabled={smtpTesting} variant="outline">
                {smtpTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Test'}
              </Button>
            </div>
            {smtpResult && (
              <div className={`mt-2 p-3 rounded-lg text-sm flex items-center gap-2 ${smtpResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                {smtpResult.success ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                {smtpResult.success ? 'Test email sent successfully!' : smtpResult.error}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Auth / Firebase Tab */}
      {websiteTab === 'auth' && (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 space-y-5" data-testid="firebase-config-section">
          <div>
            <h3 className="font-bold text-slate-800 mb-1">Firebase Google Authentication</h3>
            <p className="text-sm text-slate-500">Configure Firebase to enable Google sign-in for hosts. Get these values from Firebase Console &gt; Project Settings &gt; Web App.</p>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <Switch checked={firebase.enabled} onCheckedChange={(v) => setFirebase(f => ({ ...f, enabled: v }))} data-testid="firebase-enabled-toggle" />
            <Label className="text-sm font-medium text-slate-700">{firebase.enabled ? 'Google Sign-in Enabled' : 'Google Sign-in Disabled'}</Label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'api_key', label: 'API Key', placeholder: 'AIzaSy...' },
              { key: 'auth_domain', label: 'Auth Domain', placeholder: 'your-project.firebaseapp.com' },
              { key: 'project_id', label: 'Project ID', placeholder: 'your-project-id' },
              { key: 'storage_bucket', label: 'Storage Bucket', placeholder: 'your-project.appspot.com' },
              { key: 'messaging_sender_id', label: 'Messaging Sender ID', placeholder: '000000000000' },
              { key: 'app_id', label: 'App ID', placeholder: '1:000:web:abc123' },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <Label className="text-sm text-slate-600 mb-1 block">{label}</Label>
                <Input
                  value={firebase[key] || ''}
                  onChange={e => setFirebase(f => ({ ...f, [key]: e.target.value }))}
                  placeholder={placeholder}
                  className="h-10 border-slate-200"
                  data-testid={`firebase-${key}`}
                />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Button onClick={saveFirebase} disabled={firebaseSaving} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="save-firebase-config-btn">
              {firebaseSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Firebase Config
            </Button>
            {firebase.enabled && firebase.project_id && (
              <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Configured</span>
            )}
          </div>
        </div>
      )}

      {/* Legal Tab */}
      {websiteTab === 'legal' && (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 space-y-4">
          <div className="flex gap-2 mb-4">
            {['privacy', 'terms', 'refund'].map(tab => (
              <Button key={tab} variant={legalTab === tab ? 'default' : 'outline'} size="sm"
                className={legalTab === tab ? 'bg-slate-800 text-white' : ''} onClick={() => setLegalTab(tab)}>
                {tab === 'privacy' ? 'Privacy Policy' : tab === 'terms' ? 'Terms of Service' : 'Refund Policy'}
              </Button>
            ))}
          </div>
          <textarea className="w-full h-64 p-4 border border-slate-200 rounded-lg text-sm font-mono resize-none"
            value={website[`${legalTab}_policy`] || ''} onChange={e => setWebsite(w => ({ ...w, [`${legalTab}_policy`]: e.target.value }))}
            placeholder={`Enter your ${legalTab} policy here...`} />
          <Button onClick={saveWebsite} disabled={webSaving} className="bg-slate-800 hover:bg-slate-900 text-white">
            {webSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
          </Button>
        </div>
      )}
    </div>
  );
};
